import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from "@tanstack/react-query";
import { createContext, useContext } from "react";

import api, { ApiCatalog } from "@/api";
import { ACTIVITY_TYPE } from "@/grading/constants";
import { PARNASSUS_HOST } from "../constants";

type ChallengesActivityType =
  | typeof ACTIVITY_TYPE.QUIZ.name
  | typeof ACTIVITY_TYPE.POWER_WORDS.name
  | typeof ACTIVITY_TYPE.GUIDED_HIGHLIGHTING.name;

interface ChallengeArticleProviderProps {
  children: React.ReactNode;
  contentId: string;
}

interface ChallengeArticleData {
  article_id: string | number;
  challenge_id: string;
  topic_id: string;
  tokens: number;
  earned_tokens: number;
  absolute_url: string;
  slug: string;
  header_id: string | number;
  done_interactions: number;
  interactions: Array<{
    type: ChallengesActivityType;
    tokens: number;
    earned_tokens: number;
    is_done: boolean;
  }>;
}

interface ChallengeArticleResponse {
  success: boolean;
  error_message: any;
  data: ChallengeArticleData;
}

interface ChallengeArticleContextValue {
  articleData: ChallengeArticleResponse | null;
  refetch:
    | ((
        options?: RefetchOptions | undefined,
      ) => Promise<QueryObserverResult<ChallengeArticleResponse, Error>>)
    | null;
}

export const ChallengeArticleContext =
  createContext<ChallengeArticleContextValue>({
    articleData: null,
    refetch: null,
  });

export const ChallengeArticleProvider = ({
  children,
  contentId,
}: ChallengeArticleProviderProps) => {
  const { data: articleData = null, refetch } =
    useQuery<ChallengeArticleResponse>({
      queryKey: [ApiCatalog.challengeArticle.key, contentId],
      queryFn: async () =>
        await api({ basePath: PARNASSUS_HOST }).get(
          ApiCatalog.challengeArticle.endpoint.replace(
            "[contentId]",
            contentId,
          ),
        ),
    });

  return (
    <ChallengeArticleContext.Provider value={{ articleData, refetch }}>
      {children}
    </ChallengeArticleContext.Provider>
  );
};

const useChallengeArticleContext = () => {
  const context = useContext(ChallengeArticleContext);

  if (!context) {
    throw new Error(
      "useChallengeArticleContext must be used within an ChallengeArticleContext.",
    );
  }

  return context;
};

export default useChallengeArticleContext;
